import type { FormsApiClient } from '../api';
import type { LeadFormPublicConfig } from '../types';
import { applyThemeVars, buildCloseButton, buildForm, buildHeader, createHost, resolvePopupStyle, SHARED_PANEL_STYLE, type LeadFormTheme, type RenderHandle } from './shared';

/** A full-viewport takeover ("welcome mat") — like a lightbox that owns the whole screen instead of
 * a centered card, with an explicit "no thanks" skip link alongside the X (OptinMonster's welcome
 * mats offer both — a skip link reads as a real choice, not just a dismiss action). */
const STYLE = `
  ${SHARED_PANEL_STYLE}
  .panel {
    position: fixed; inset: 0; z-index: 2147483000; border-radius: 0; border: none;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    text-align: center; padding: 40px 24px; overflow-y: auto;
  }
  .panel .image { max-width: 320px; margin-left: auto; margin-right: auto; }
  .panel .headline { font-size: 32px; }
  .panel form { width: min(420px, 100%); text-align: left; margin-top: 8px; }
  .skip {
    margin-top: 16px; background: none; border: none; cursor: pointer;
    color: currentColor; opacity: 0.65; font-size: 13px; text-decoration: underline;
  }
`;

export function renderScreenFiller(
  form: LeadFormPublicConfig,
  client: FormsApiClient,
  onSubmit: (data: Record<string, unknown>) => Promise<boolean>,
): RenderHandle {
  const theme = form.theme as LeadFormTheme;
  const resolved = resolvePopupStyle(theme);
  const { host, root } = createHost(STYLE);

  const panel = document.createElement('div');
  panel.className = 'panel';
  applyThemeVars(panel, resolved);

  panel.appendChild(buildCloseButton(close));
  for (const el of buildHeader(form, client, panel)) {
    panel.appendChild(el);
  }
  panel.appendChild(buildForm(form.fields, theme.ctaLabel || 'Submit', onSubmit, close));

  const skip = document.createElement('button');
  skip.type = 'button';
  skip.className = 'skip';
  skip.textContent = 'No thanks';
  skip.addEventListener('click', close);
  panel.appendChild(skip);

  root.appendChild(panel);

  function close() {
    host.remove();
  }

  return { close };
}
